<!-- src/apps/secret/components/SecretReceiptTable.vue -->

<!--
  Console-style dashboard receipts feed.
  Lists pending and revealed receipts using SecretReceiptTableItem.
  Self-evident row design — no table headers needed.
-->

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import OIcon from '@/shared/components/icons/OIcon.vue';
import SecretReceiptTableItem from '@/apps/secret/components/SecretReceiptTableItem.vue';
import type { ReceiptList } from '@/schemas/shapes/v3/receipt';

const { t } = useI18n();

interface Props {
  pendingReceipts: ReceiptList[];
  revealedReceipts: ReceiptList[];
  isLoading: boolean;
}

defineProps<Props>();
</script>

<template>
  <div v-cloak>
    <template v-if="isLoading">
      <!-- Loading indicator -->
      <p class="text-sm text-day-ink-muted dark:text-night-ink-muted">
        {{ t('web.COMMON.loading_ellipses') }}
      </p>
    </template>
    <template v-else>
      <!--
        Sent (not yet revealed). The mock stacks free-standing cards with a 12px
        gutter — no shared container, no separators; SecretReceiptTableItem now
        carries its own card chrome.
      -->
      <section
        class="mb-11"
        aria-labelledby="pending-receipts-heading">
        <ul
          v-if="pendingReceipts && pendingReceipts.length > 0"
          class="flex flex-col gap-3"
          role="list"
          :aria-label="t('web.dashboard.title_not_received')">
          <SecretReceiptTableItem
            v-for="(item, idx) in pendingReceipts"
            :key="item.identifier ?? item.shortid ?? idx"
            :secret-receipt="item"
            :index="idx + 1" />
        </ul>

        <!-- Empty state for not received -->
        <div
          v-else
          class="flex flex-col items-center gap-3.5 border border-dashed border-day-line-2 bg-day px-6 py-14 text-center dark:border-white/[0.13] dark:bg-night-100">
          <OIcon
            collection="heroicons"
            name="document-text"
            class="size-[26px] text-day-ink-faint/50 dark:text-white/25"
            aria-hidden="true" />
          <p class="font-sans text-[15px] text-day-ink-faint dark:text-night-ink-faint">
            {{ t('web.COMMON.go_on_then') }}
            <router-link
              to="/"
              class="text-brand-600 hover:underline dark:text-brand-500">
              {{ t('web.COMMON.share_a_secret') }}
            </router-link>
          </p>
        </div>
      </section>

      <!-- Received (revealed) -->
      <section aria-labelledby="revealed-receipts-heading">
        <h2
          id="revealed-receipts-heading"
          class="mb-4 font-brand text-xl font-normal text-day-ink dark:text-night-ink">
          {{ t('web.dashboard.title_received') }}
        </h2>

        <ul
          v-if="revealedReceipts && revealedReceipts.length > 0"
          class="flex flex-col gap-3"
          role="list"
          :aria-label="t('web.dashboard.title_received')">
          <SecretReceiptTableItem
            v-for="(item, idx) in revealedReceipts"
            :key="item.identifier ?? item.shortid ?? idx"
            :secret-receipt="item"
            :index="idx + 1" />
        </ul>

        <!--
          Empty state for received. The mock draws this one DASHED — an outline
          of a container that has nothing in it yet, distinct from the solid
          cards above.
        -->
        <div
          v-else
          class="flex flex-col items-center gap-3.5 border border-dashed border-day-line-2 bg-day px-6 py-14 dark:border-white/[0.13] dark:bg-night-100">
          <!--
            `heroicons-inbox` is NOT in this app's sprite sheet — the previous
            markup asked for it and rendered an empty <use>, so the received
            empty state had no icon at all. `inbox-arrow-down` is the closest
            id that actually ships, and it reads as the mock's inbox tray.
          -->
          <OIcon
            collection="heroicons"
            name="inbox-arrow-down"
            class="size-[26px] text-day-ink-faint/50 dark:text-white/25"
            aria-hidden="true" />
          <p class="font-sans text-[15px] text-day-ink-faint dark:text-night-ink-faint">
            {{ t('web.COMMON.word_none') }}
          </p>
        </div>
      </section>
    </template>
  </div>
</template>
